import React, { forwardRef } from "react";
import { Box, Typography } from "@mui/material";
import { VirtuosoGrid, GridComponents } from "react-virtuoso";
import AppCircularProgress from "../components/common/AppCircularProgress";
import PageError from "../components/common/PageError";

interface VirtualizedCardGridProps<T> {
  data: T[];
  loading: boolean;
  error: string | null;
  emptyMessage?: string;
  onRetry: () => void;
  renderCard: (item: T, index: number) => React.ReactNode;
}

// Custom Grid components to maintain your responsive multi-column layout
const gridComponents: GridComponents = {
  List: forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({ style, children, ...props }, ref) => (
      <Box
        ref={ref}
        {...props}
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(4, 1fr)",
          },
          gap: 3,
          pb: 4,
          ...style,
        }}
      >
        {children}
      </Box>
    ),
  ),
  Item: forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({ children, ...props }, ref) => (
      <div ref={ref} {...props}>
        {children}
      </div>
    ),
  ),
};

export function VirtualizedCardGrid<T>({
  data,
  loading,
  error,
  emptyMessage = "No records found",
  onRetry,
  renderCard,
}: VirtualizedCardGridProps<T>) {
  if (loading) {
    return <AppCircularProgress />;
  }

  if (error) {
    return <PageError error={error} onRetry={onRetry} />;
  }

  if (data.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography color="text.secondary">{emptyMessage}</Typography>
      </Box>
    );
  }

  return (
    <VirtuosoGrid
      useWindowScroll
      data={data}
      components={gridComponents}
      itemContent={(index, item) => renderCard(item, index)}
    />
  );
}

export default VirtualizedCardGrid;
